import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { MdAddBox, MdDelete, MdEdit } from 'react-icons/md';
import fetchWrapper from '../lib/fetch-wrapper';

type DistroForm = {
    Property: string;
    HrDistro: string;
    BenefitsDistro: string;
    Active: boolean;
    Notes: string;
};

const emptyForm: DistroForm = {
    Property: '',
    HrDistro: '',
    BenefitsDistro: '',
    Active: true,
    Notes: '',
};

const isBlankOrEmail = (value: string) =>
    !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const DistroPage = () => {
    const [records, setRecords] = useState<any[]>([]);
    const [propertyOptions, setPropertyOptions] = useState<{ value: string; label: string }[]>([]);
    const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
    const [formData, setFormData] = useState<DistroForm>(emptyForm);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        void fetchRecords();
        void fetchProperties();
    }, []);

    const usedProperties = useMemo(
        () => new Set(records.map((r) => String(r.data?.Property || r.RowKey || '').toUpperCase())),
        [records]
    );

    const availablePropertyOptions = useMemo(() => {
        if (selectedRecord) {
            return propertyOptions;
        }
        return propertyOptions.filter((opt) => !usedProperties.has(opt.value.toUpperCase()));
    }, [propertyOptions, usedProperties, selectedRecord]);

    const fetchProperties = async () => {
        try {
            const fw = new fetchWrapper();
            const response: any = await fw.getProperties();
            const formatted = response.data.records
                .map((record: any) => ({
                    value: record.data.Name,
                    label: record.data.Name,
                }))
                .sort((a: any, b: any) => a.label.localeCompare(b.label));
            setPropertyOptions(formatted);
        } catch {
            toast({ title: 'Error fetching properties', status: 'error', duration: 3000 });
        }
    };

    const fetchRecords = async () => {
        try {
            const fw = new fetchWrapper();
            const response: any = await fw.fetchRecords('/api/distro/records');
            setRecords(response.data.records || []);
        } catch {
            toast({ title: 'Error fetching distro records', status: 'error', duration: 3000 });
        }
    };

    const deleteRecord = async (rowKey: string) => {
        if (!window.confirm(`Delete distro for ${rowKey}?`)) return;
        try {
            const fw = new fetchWrapper();
            await fw.deleteRecord(`/api/distro/remove/${rowKey}`);
            toast({ title: 'Distro deleted', status: 'success', duration: 3000 });
            void fetchRecords();
        } catch {
            toast({ title: 'Error deleting distro', status: 'error', duration: 3000 });
        }
    };

    const handleClose = () => {
        setSelectedRecord(null);
        setFormData(emptyForm);
        onClose();
    };

    const handleAdd = () => {
        setSelectedRecord(null);
        setFormData(emptyForm);
        onOpen();
    };

    const handleEdit = (record: any) => {
        setSelectedRecord(record);
        setFormData({
            Property: record.data.Property || record.RowKey,
            HrDistro: record.data.HrDistro || '',
            BenefitsDistro: record.data.BenefitsDistro || '',
            Active: !!record.data.Active,
            Notes: record.data.Notes || '',
        });
        onOpen();
    };

    const handleSubmit = async () => {
        if (!formData.Property.trim()) {
            toast({
                title: 'Validation Error',
                description: 'Property is required.',
                status: 'error',
                duration: 3000,
            });
            return;
        }
        if (!isBlankOrEmail(formData.HrDistro) || !isBlankOrEmail(formData.BenefitsDistro)) {
            toast({
                title: 'Invalid Email',
                description: 'HrDistro and BenefitsDistro must be empty or valid emails.',
                status: 'error',
                duration: 3000,
            });
            return;
        }
        try {
            const fw = new fetchWrapper();
            const payload = {
                Property: formData.Property.trim().toUpperCase(),
                HrDistro: formData.HrDistro.trim(),
                BenefitsDistro: formData.BenefitsDistro.trim(),
                Active: formData.Active,
                Notes: formData.Notes.trim(),
            };
            if (selectedRecord) {
                await fw.editRecord('/api/distro/edit', {
                    RowKey: selectedRecord.RowKey,
                    data: payload,
                });
                toast({ title: 'Distro updated', status: 'success', duration: 3000 });
            } else {
                await fw.addRecord('/api/distro/add', payload);
                toast({ title: 'Distro added', status: 'success', duration: 3000 });
            }
            void fetchRecords();
            handleClose();
        } catch (error: any) {
            const detail = error?.response?.data?.detail || 'Error saving distro';
            toast({ title: String(detail), status: 'error', duration: 4000 });
        }
    };

    return (
        <Box mx="auto" mt="6" bg="#ffffff" height="100%" m="0" px="30px" pt="30px" overflowY="auto">
            <Button
                variant="brand"
                size="lg"
                height="4rem"
                leftIcon={<MdAddBox size="1.5em" />}
                onClick={handleAdd}
                isDisabled={availablePropertyOptions.length === 0}
            >
                Add Distro
            </Button>
            <Table variant="simple" mt="4">
                <Thead>
                    <Tr>
                        <Th>Property</Th>
                        <Th>HR Distro</Th>
                        <Th>Benefits Distro</Th>
                        <Th>Active</Th>
                        <Th>Notes</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {records.map((record: any) => (
                        <Tr key={record.RowKey}>
                            <Td>{record.data.Property || record.RowKey}</Td>
                            <Td>{record.data.HrDistro || '—'}</Td>
                            <Td>{record.data.BenefitsDistro || '—'}</Td>
                            <Td>{record.data.Active ? 'Yes' : 'No'}</Td>
                            <Td>{record.data.Notes || ''}</Td>
                            <Td>
                                <Button
                                    bg="transparent"
                                    leftIcon={<MdEdit color="black" />}
                                    onClick={() => handleEdit(record)}
                                />
                                <Button
                                    bg="transparent"
                                    leftIcon={<MdDelete color="black" />}
                                    onClick={() => deleteRecord(record.RowKey)}
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>

            <Modal isOpen={isOpen} size="2xl" closeOnOverlayClick={false} onClose={handleClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{selectedRecord ? 'Edit Distro' : 'Add Distro'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isRequired mb={4}>
                            <FormLabel>Property</FormLabel>
                            <Select
                                placeholder="Select property"
                                value={formData.Property}
                                isDisabled={!!selectedRecord}
                                onChange={(e) =>
                                    setFormData({ ...formData, Property: e.target.value })
                                }
                            >
                                {(selectedRecord ? propertyOptions : availablePropertyOptions).map(
                                    (opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    )
                                )}
                            </Select>
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>{'HR Distro (<<<hr_distro>>>)'}</FormLabel>
                            <Input
                                value={formData.HrDistro}
                                onChange={(e) =>
                                    setFormData({ ...formData, HrDistro: e.target.value })
                                }
                                placeholder="e.g. WCA-AskHR@windcreek.com"
                            />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>{'Benefits Distro (<<<benefits_distro>>>)'}</FormLabel>
                            <Input
                                value={formData.BenefitsDistro}
                                onChange={(e) =>
                                    setFormData({ ...formData, BenefitsDistro: e.target.value })
                                }
                                placeholder="e.g. PCI-Benefits@pcigaming.com"
                            />
                        </FormControl>
                        <FormControl mb={4}>
                            <Checkbox
                                isChecked={formData.Active}
                                onChange={(e) =>
                                    setFormData({ ...formData, Active: e.target.checked })
                                }
                            >
                                Active
                            </Checkbox>
                        </FormControl>
                        <FormControl mb={2}>
                            <FormLabel>Notes</FormLabel>
                            <Input
                                value={formData.Notes}
                                onChange={(e) =>
                                    setFormData({ ...formData, Notes: e.target.value })
                                }
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="red" onClick={handleSubmit}>
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default DistroPage;
